// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import { useCallback, useEffect, useState } from "react";
import spaceService from "../services/spaceService";

/**
 * Bridge between the SVG editor's local element list and the backend's
 * vhp_space records.
 */
const normalizeSpaceFromApi = (raw) => ({
  id: raw.id,
  scenario_id: raw.scenario_id,
  space_code: raw.space_code ?? "",
  space_number:
    raw.space_number != null && raw.space_number !== ""
      ? String(raw.space_number)
      : "",
  name: raw.name ?? "",
  description: raw.description ?? "",
  description_html: raw.description_html ?? "",
  space_type: raw.space_type ?? "event_area",
  capacity: raw.capacity ?? null,
  capacity_unit: raw.capacity_unit ?? "persons",
  svg_path: raw.svg_path ?? "",
  svg_element_id: raw.svg_element_id ?? "",
  gps_lat: raw.gps_lat ?? null,
  gps_lng: raw.gps_lng ?? null,
  orientation: raw.orientation ?? null,
  area_sqm: raw.area_sqm ?? null,
  length_m: raw.length_m ?? null,
  width_m: raw.width_m ?? null,
  height_m: raw.height_m ?? null,
  equipment: raw.equipment ?? {},
  amenities: Array.isArray(raw.amenities) ? raw.amenities : [],
  access_control_type: raw.access_control_type ?? null,
  access_control_device_id: raw.access_control_device_id ?? "",
  is_active: raw.is_active !== false,
  is_bookable: raw.is_bookable !== false,
  maintenance_status: raw.maintenance_status ?? "operational",
});

/**
 * Convert an editor element (rect/circle/polygon) to an SVG path d-string.
 */
const elementToSvgPath = (element) => {
  if (!element) return "";
  switch (element.type) {
    case "rect": {
      const x = Number(element.x) || 0;
      const y = Number(element.y) || 0;
      const w = Number(element.width) || 0;
      const h = Number(element.height) || 0;
      return `M ${x} ${y} L ${x + w} ${y} L ${x + w} ${y + h} L ${x} ${y + h} Z`;
    }
    case "circle": {
      const cx = Number(element.cx) || 0;
      const cy = Number(element.cy) || 0;
      const r = Number(element.r) || 0;
      const k = 0.5522847498;
      const ox = r * k;
      const oy = r * k;
      return [
        `M ${cx - r} ${cy}`,
        `C ${cx - r} ${cy - oy} ${cx - ox} ${cy - r} ${cx} ${cy - r}`,
        `C ${cx + ox} ${cy - r} ${cx + r} ${cy - oy} ${cx + r} ${cy}`,
        `C ${cx + r} ${cy + oy} ${cx + ox} ${cy + r} ${cx} ${cy + r}`,
        `C ${cx - ox} ${cy + r} ${cx - r} ${cy + oy} ${cx - r} ${cy}`,
        "Z",
      ].join(" ");
    }
    case "polygon": {
      const pts = Array.isArray(element.points) ? element.points : [];
      if (pts.length < 2) return "";
      const [first, ...rest] = pts.map(
        (p) => `${p.x ?? p.lng ?? 0} ${p.y ?? p.lat ?? 0}`,
      );
      return `M ${first} ${rest.map((s) => `L ${s}`).join(" ")} Z`;
    }
    default:
      return element.svg_path || "";
  }
};

/**
 * Capture the element's original shape type + geometry so a save→reload
 * round-trip can restore a rect/circle exactly, instead of degrading every
 * shape to a generic polygon (the DB only stores an svg_path). Persisted under
 * equipment._shape (equipment is preserved across editor saves).
 */
const elementShapeDescriptor = (element) => {
  if (!element) return null;
  switch (element.type) {
    case "rect":
      return {
        type: "rect",
        x: Number(element.x) || 0,
        y: Number(element.y) || 0,
        width: Number(element.width) || 0,
        height: Number(element.height) || 0,
      };
    case "circle":
      return {
        type: "circle",
        cx: Number(element.cx) || 0,
        cy: Number(element.cy) || 0,
        r: Number(element.r) || 0,
      };
    case "polygon":
      return { type: "polygon" };
    default:
      return element.type ? { type: element.type } : null;
  }
};

/**
 * Try to parse an SVG path d-string back into a polygon-points array.
 */
const parsePathToPolygonPoints = (d) => {
  if (typeof d !== "string" || !d.trim()) return null;
  const tokens = d.match(/[MLlmZz]|-?\d*\.?\d+/g);
  if (!tokens) return null;
  const pts = [];
  let i = 0;
  while (i < tokens.length) {
    const t = tokens[i];
    if (t === "M" || t === "L" || t === "m" || t === "l") {
      const x = parseFloat(tokens[i + 1]);
      const y = parseFloat(tokens[i + 2]);
      if (Number.isFinite(x) && Number.isFinite(y)) {
        pts.push({ x, y });
        i += 3;
        continue;
      }
    }
    if (t === "Z" || t === "z") {
      i += 1;
      continue;
    }
    return null; // unsupported command
  }
  return pts.length >= 3 ? pts : null;
};

/**
 * If four path points form an axis-aligned rectangle, return { x, y, width, height }.
 * Used so saved rects survive reload when equipment._shape is missing.
 */
const axisAlignedRectFromPoints = (points, eps = 0.5) => {
  if (!Array.isArray(points) || points.length < 4) return null;
  let pts = points;
  if (pts.length >= 5) {
    const first = pts[0];
    const last = pts[pts.length - 1];
    if (Math.hypot(first.x - last.x, first.y - last.y) < eps) {
      pts = pts.slice(0, -1);
    }
  }
  if (pts.length !== 4) return null;

  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const width = maxX - minX;
  const height = maxY - minY;
  if (!(width > eps && height > eps)) return null;

  const onCorner = (p) =>
    (Math.abs(p.x - minX) < eps || Math.abs(p.x - maxX) < eps) &&
    (Math.abs(p.y - minY) < eps || Math.abs(p.y - maxY) < eps);
  if (!pts.every(onCorner)) return null;

  const cornerKeys = new Set(
    pts.map(
      (p) =>
        `${Math.abs(p.x - minX) < eps ? "0" : "1"}-${Math.abs(p.y - minY) < eps ? "0" : "1"}`,
    ),
  );
  if (cornerKeys.size !== 4) return null;

  return { x: minX, y: minY, width, height };
};

/**
 * Compute physical measurements from an editor element.
 */
const inferMeasurements = (element) => {
  if (!element) return {};
  const orientation = Number.isFinite(Number(element.rotation))
    ? Math.round(Number(element.rotation))
    : 0;

  switch (element.type) {
    case "rect": {
      const w = Math.abs(Number(element.width) || 0);
      const h = Math.abs(Number(element.height) || 0);
      return {
        width_m: w,
        length_m: h,
        area_sqm: w * h,
        orientation,
      };
    }
    case "circle": {
      const r = Math.abs(Number(element.r) || 0);
      return {
        width_m: 2 * r,
        length_m: 2 * r,
        area_sqm: Math.PI * r * r,
        orientation,
      };
    }
    case "polygon": {
      const pts = Array.isArray(element.points) ? element.points : [];
      if (pts.length < 3) return { orientation };
      const xs = pts.map((p) => p.x ?? p.lng ?? 0);
      const ys = pts.map((p) => p.y ?? p.lat ?? 0);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      let area = 0;
      for (let i = 0; i < pts.length; i++) {
        const j = (i + 1) % pts.length;
        area += xs[i] * ys[j] - xs[j] * ys[i];
      }
      return {
        width_m: Math.abs(maxX - minX),
        length_m: Math.abs(maxY - minY),
        area_sqm: Math.abs(area / 2),
        orientation,
      };
    }
    default:
      return { orientation };
  }
};

/**
 * Map a normalized API space into an editor element so the existing renderer draws it.
 */
const spaceToElement = (space, layer = "main", opts = {}) => {
  const points = parsePathToPolygonPoints(space.svg_path);
  const style = space.equipment?._style || {};
  const base = {
    id: space.svg_element_id || `space-${space.id}`,
    layer,
    fill: style.fill || "#e3f2fd",
    stroke: style.stroke || "#1976d2",
    strokeWidth: style.strokeWidth ?? 2,
    rotation: style.rotation ?? space.orientation ?? 0,
    ...(style.fontSize ? { fontSize: style.fontSize } : {}),
    locked: false,
    visible: true,
    label: space.name || space.space_code || "",
    spaceId: space.id,
    space_code: space.space_code,
    space_number: space.space_number,
    name: space.name,
    description: space.description,
    description_html: space.description_html,
    space_type: space.space_type,
    capacity: space.capacity,
    capacity_unit: space.capacity_unit,
    svg_path: space.svg_path,
    gps_lat: space.gps_lat,
    gps_lng: space.gps_lng,
    orientation: space.orientation,
    area_sqm: space.area_sqm,
    length_m: space.length_m,
    width_m: space.width_m,
    height_m: space.height_m,
    equipment: space.equipment,
    amenities: space.amenities,
    access_control_type: space.access_control_type,
    access_control_device_id: space.access_control_device_id,
    is_active: space.is_active,
    is_bookable: space.is_bookable,
    maintenance_status: space.maintenance_status,
  };

  // Prefer the saved shape descriptor so a rect/circle survives the
  // save→reload round-trip instead of degrading to a polygon.
  const shape = space.equipment?._shape;
  if (shape && shape.type === "rect") {
    const nums = [shape.x, shape.y, shape.width, shape.height].map(Number);
    if (nums.every((n) => Number.isFinite(n))) {
      const [x, y, width, height] = nums;
      return { ...base, type: "rect", x, y, width, height };
    }
  }
  if (shape && shape.type === "circle") {
    const cx = Number(shape.cx);
    const cy = Number(shape.cy);
    const r = Number(shape.r ?? shape.radius);
    if ([cx, cy, r].every((n) => Number.isFinite(n))) {
      return { ...base, type: "circle", cx, cy, r };
    }
  }

  // Fallback: rects are stored as a 4-point path. Rebuild as type "rect" so
  // resize handles still work after save→reload when _shape was stripped.
  if (points && points.length >= 4) {
    const rect = axisAlignedRectFromPoints(points);
    if (rect) {
      return { ...base, type: "rect", ...rect };
    }
  }

  if (points && points.length >= 3) {
    return { ...base, type: "polygon", points };
  }
  // Geometry-less space (no svg_path): drop a default rect in the CENTRE of the
  // visible viewBox so it's findable, instead of a hard-coded (40,40) that may
  // sit outside the scenario's viewBox. `index` staggers multiples so they
  // don't perfectly overlap.
  const w = 80;
  const h = 50;
  const vb = opts.viewBox;
  const i = Number(opts.index) || 0;
  const stagger = (i % 6) * 24;
  const cx = vb ? vb.x + vb.width / 2 : 40 + w / 2;
  const cy = vb ? vb.y + vb.height / 2 : 40 + h / 2;
  return {
    ...base,
    type: "rect",
    x: cx - w / 2 + stagger,
    y: cy - h / 2 + stagger,
    width: w,
    height: h,
  };
};

const numOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const intOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};

// Build an SVG→[lng,lat] transform from two georeferenced pins.
export const makeSvgToLatLng = (pin1, pin2) => {
  const g1 = pin1?.gps;
  const g2 = pin2?.gps;
  if (!g1 || !g2) return null;
  const lat1 = Number(g1.latitude);
  const lng1 = Number(g1.longitude);
  const lat2 = Number(g2.latitude);
  const lng2 = Number(g2.longitude);
  if ([lat1, lng1, lat2, lng2].some((v) => !Number.isFinite(v))) return null;

  const M_PER_DEG_LAT = 111320;
  const mPerDegLng = M_PER_DEG_LAT * Math.cos((lat1 * Math.PI) / 180);

  const east2 = (lng2 - lng1) * mPerDegLng;
  const north2 = (lat2 - lat1) * M_PER_DEG_LAT;
  const dx = pin2.x - pin1.x;
  const dyUp = -(pin2.y - pin1.y);

  const pixLen = Math.hypot(dx, dyUp);
  const geoLen = Math.hypot(east2, north2);
  if (!(pixLen > 0) || !(geoLen > 0)) return null;

  const scale = geoLen / pixLen;
  const theta = Math.atan2(north2, east2) - Math.atan2(dyUp, dx);
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);

  return (sx, sy) => {
    const vx = sx - pin1.x;
    const vyUp = -(sy - pin1.y);
    const east = scale * (cos * vx - sin * vyUp);
    const north = scale * (sin * vx + cos * vyUp);
    return [lng1 + east / mPerDegLng, lat1 + north / M_PER_DEG_LAT];
  };
};

// Polygon vertices of an element in SVG coordinates (rect corners w/ rotation, or polygon points).
const getElementVertices = (el) => {
  if (Array.isArray(el.points) && el.points.length >= 3) {
    return el.points.map((p) =>
      Array.isArray(p) ? { x: p[0], y: p[1] } : { x: p.x, y: p.y },
    );
  }
  const x = el.x ?? el.cx ?? 0;
  const y = el.y ?? el.cy ?? 0;
  const w = el.width ?? (el.r ? el.r * 2 : 0);
  const h = el.height ?? (el.r ? el.r * 2 : 0);
  if (!(w > 0) || !(h > 0)) return null;
  let corners = [
    { x, y },
    { x: x + w, y },
    { x: x + w, y: y + h },
    { x, y: y + h },
  ];
  const rot = el.rotation || 0;
  if (rot) {
    const a = (rot * Math.PI) / 180;
    const c = Math.cos(a);
    const s = Math.sin(a);
    const cxc = x + w / 2;
    const cyc = y + h / 2;
    corners = corners.map((p) => {
      const px = p.x - cxc;
      const py = p.y - cyc;
      return { x: cxc + px * c - py * s, y: cyc + px * s + py * c };
    });
  }
  return corners;
};

// GeoJSON Polygon (WGS84) for the element, or null when no georeference
const buildBoundaryGeoJson = (element, svgToLatLng) => {
  if (!svgToLatLng) return null;
  const verts = getElementVertices(element);
  if (!verts || verts.length < 3) return null;
  const ring = verts.map((v) => svgToLatLng(v.x, v.y));
  ring.push(ring[0]); // close the ring
  return { type: "Polygon", coordinates: [ring] };
};

const elementToSpacePayload = (element, scenarioId, svgToLatLng) => {
  const measured = inferMeasurements(element);
  return {
    scenario_id: scenarioId,
    boundary_polygon: buildBoundaryGeoJson(element, svgToLatLng),
    space_code: element.space_code || String(element.id),
    space_number: intOrNull(element.space_number),
    name: element.name || element.label || "",
    description: element.description ?? null,
    description_html: element.description_html ?? null,
    space_type: element.space_type || "event_area",
    capacity: intOrNull(element.capacity),
    capacity_unit: element.capacity_unit || "persons",
    // The "SVG Code" field (space_code) doubles as the SVG element id so each
    // space carries its own human-defined id; falls back to the generated id.
    svg_element_id:
      (typeof element.space_code === "string" && element.space_code.trim()) ||
      String(element.id),
    svg_path: elementToSvgPath(element),
    gps_lat: numOrNull(element.gps_lat),
    gps_lng: numOrNull(element.gps_lng),
    orientation:
      intOrNull(element.orientation) ??
      (Number.isFinite(measured.orientation) ? measured.orientation : null),
    area_sqm: numOrNull(element.area_sqm) ?? measured.area_sqm ?? null,
    length_m: numOrNull(element.length_m) ?? measured.length_m ?? null,
    width_m: numOrNull(element.width_m) ?? measured.width_m ?? null,
    height_m: numOrNull(element.height_m),
    equipment: {
      ...(element.equipment && typeof element.equipment === "object"
        ? element.equipment
        : {}),
      _style: {
        fill: element.fill || "#e3f2fd",
        stroke: element.stroke || "#1976d2",
        strokeWidth: element.strokeWidth ?? 2,
        rotation: element.rotation ?? 0,
        fontSize: element.fontSize ?? null,
      },
      _shape: elementShapeDescriptor(element),
    },
    amenities: Array.isArray(element.amenities) ? element.amenities : [],
    access_control_type: element.access_control_type ?? null,
    access_control_device_id: element.access_control_device_id || null,
    is_active: element.is_active !== false,
    is_bookable: element.is_bookable !== false,
    maintenance_status: element.maintenance_status || "operational",
  };
};

const useScenarioSpaces = (scenarioId) => {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!scenarioId) {
      setSpaces([]);
      return [];
    }
    setLoading(true);
    setError(null);
    try {
      const response = await spaceService.listSpaces({ scenarioId });
      const items = Array.isArray(response)
        ? response
        : (response?.items ?? response?.data ?? response?.results ?? []);
      const normalized = items.map(normalizeSpaceFromApi);
      setSpaces(normalized);
      return normalized;
    } catch (err) {
      console.error("Error loading spaces:", err);
      setError(err);
      setSpaces([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [scenarioId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  /**
   * Diff the canvas against the loaded `spaces` list and persist:
   * - Element with no spaceId → POST (create).
   * - Element with spaceId matching a loaded row → PUT (update).
   * - Loaded row whose spaceId is NOT referenced by any element → DELETE.
   */
  const persistAll = useCallback(
    async (elements, svgToLatLng = null) => {
      if (!scenarioId) {
        throw new Error("Cannot persist spaces without a scenarioId");
      }

      const allElements = (elements || []).filter(Boolean);
      const elementSpaceIds = new Set(
        allElements.map((el) => el.spaceId).filter(Boolean),
      );
      const loadedById = new Map(spaces.map((s) => [s.id, s]));

      const toCreate = allElements.filter((el) => !el.spaceId);
      const toUpdate = allElements.filter(
        (el) => el.spaceId && loadedById.has(el.spaceId),
      );
      const toDelete = spaces.filter((s) => !elementSpaceIds.has(s.id));

      const errors = [];

      const created = await Promise.all(
        toCreate.map((el) =>
          spaceService
            .createSpace(elementToSpacePayload(el, scenarioId, svgToLatLng))
            .then((res) => ({ tempId: el.id, space: res }))
            .catch((e) => {
              errors.push({ op: "create", element: el.id, error: e });
              return null;
            }),
        ),
      );

      const updated = await Promise.all(
        toUpdate.map((el) =>
          spaceService
            .updateSpace(
              el.spaceId,
              elementToSpacePayload(el, scenarioId, svgToLatLng),
            )
            .then((res) => ({ spaceId: el.spaceId, space: res }))
            .catch((e) => {
              errors.push({ op: "update", spaceId: el.spaceId, error: e });
              return null;
            }),
        ),
      );

      const deleted = await Promise.all(
        toDelete.map((s) =>
          spaceService
            .deleteSpace(s.id)
            .then(() => s.id)
            .catch((e) => {
              errors.push({ op: "delete", spaceId: s.id, error: e });
              return null;
            }),
        ),
      );

      await refetch();

      return {
        created: created.filter(Boolean),
        updated: updated.filter(Boolean),
        deleted: deleted.filter(Boolean),
        errors,
      };
    },
    [scenarioId, spaces, refetch],
  );

  return { spaces, loading, error, refetch, persistAll };
};

export default useScenarioSpaces;
export {
  normalizeSpaceFromApi,
  elementToSpacePayload,
  elementToSvgPath,
  spaceToElement,
  inferMeasurements,
};
